/**
 * TreatmentAdminPanel — physician capture of administered cell counts /
 * doses + the lot QC certificate that backs them (R2 Phase 1, Jun 11 2026).
 *
 * Mounted on the contact's Physician tab. Self-gates on
 * treatment_administration:read (physician + sysadmin). Lets the physician
 * record what was administered (count, unit, viability, lot) and view the
 * history; for a lot, shows whether a QC certificate is on file (the donor
 * screening / sterility / viability evidence Jude asked to see) and lets
 * the physician record the headline QC results + open an attached PDF.
 *
 * The patient-facing Certificate of Administration is Phase 2 (pending
 * compliance wording) — this panel is internal only.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Syringe, FileCheck2, Loader2, AlertCircle, CheckCircle2, FlaskConical, AlertTriangle, X } from "lucide-react";

const MODALITIES = [
  { v: "msc", label: "MSC stem cells" },
  { v: "muse", label: "Muse cells" },
  { v: "exosome", label: "Exosomes" },
  { v: "prp", label: "PRP (autologous)" },
  { v: "nk", label: "NK cells" },
  { v: "peptide", label: "Peptide" },
  { v: "other", label: "Other" },
];
const ROUTES = [
  ["iv_infusion", "IV infusion"], ["localized_injection", "Localized injection"],
  ["intra_articular", "Intra-articular"], ["subcutaneous", "Subcutaneous"],
  ["intramuscular", "Intramuscular"], ["topical", "Topical"], ["other", "Other"],
];
const UNITS = [
  ["million_cells", "million cells"], ["cells", "cells"], ["cells_per_kg", "cells/kg"],
  ["viable_cells_per_ml", "viable cells/mL"], ["particles_per_ml", "particles/mL"],
  ["mg", "mg"], ["ml", "mL"], ["iu", "IU"], ["other", "other"],
];
// Modalities that carry a donor/manufacturer QC cert (PRP/other do not).
const CERT_MODALITIES = new Set(["msc", "muse", "exosome", "nk", "peptide"]);

function hasPerm(user, p) { return new Set(user?.permissions || []).has(p); }

export default function TreatmentAdminPanel({ contactId, contactData, user }) {
  const canRead = hasPerm(user, "treatment_administration:read");
  const canWrite = hasPerm(user, "treatment_administration:write");
  const canQc = hasPerm(user, "qc_certificate:write");

  const proposalId = contactData?.activeProposalId || contactData?.proposal_id || null;
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    modality: "msc", route: "iv_infusion", administeredCount: "", countUnit: "million_cells",
    viabilityPct: "", lotNumber: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  /**
   * ⛔ NOT A TOAST — Card P83.
   *
   * `msg` above is a transient confirmation. This is the opposite: the stock
   * was not decremented, the on-hand figure is now wrong, and somebody has to
   * act. It stays on the record until the operator dismisses it, because the
   * person who can fix it may not be the person looking at the screen when it
   * appears — and a message that clears itself is a message nobody has to
   * read.
   */
  const [stockWarning, setStockWarning] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    if (!contactId || !canRead) return;
    try {
      const r = await window.zignagenixAPI.invoke("treatment-administrations:list", { contactId });
      if (Array.isArray(r)) setRows(r);
    } catch (_) { /* degrade quietly */ }
  }, [contactId, canRead]);
  useEffect(() => { load(); }, [load]);

  async function record() {
    setErr(""); setMsg("");
    if (!proposalId) { setErr("No treatment proposal is linked to this contact yet — create the proposal first."); return; }
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("treatment-administrations:record", {
        proposalId, contactId, ...form,
      });
      if (r && r.error) setErr(r.message || r.code);
      else {
        setMsg("Administration recorded.");
        // ⚠ Set AFTER the success branch is entered, never instead of it: the
        // treatment WAS recorded, and the warning is about stock only.
        setStockWarning(r && r.inventoryWarning ? r.inventoryWarning : null);
        setForm((f) => ({ ...f, administeredCount: "", viabilityPct: "", notes: "" }));
        await load();
      }
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  const openCertDoc = useCallback(async (qcId) => {
    try {
      const r = await window.zignagenixAPI.invoke("qc-certs:get-doc-url", { id: qcId });
      if (r && r.url) window.open(r.url, "_blank", "noopener");
      else setErr(r?.message || r?.code || "No document on file for this lot");
    } catch (e) { setErr(e.message); }
  }, []);

  if (!canRead) return null;

  return (
    <div style={{ border: "1px solid #e1e6ed", borderRadius: 6, background: "#fff", padding: 14, marginTop: 14 }}>
      {/* ⛔ Stock was not decremented. Amber, persistent, dismissible —
          rendered at the top of the panel so it is not below the fold after
          the list reloads. */}
      {stockWarning && (
        <div
          data-testid="stock-not-updated-warning"
          style={{
            display: "flex", alignItems: "flex-start", gap: 10,
            padding: "10px 12px", marginBottom: 12,
            background: "#FEF3C7", border: "1px solid #F59E0B",
            borderRadius: 6, fontSize: 12, color: "#78350F", lineHeight: 1.5,
          }}
        >
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ flex: 1 }}>
            <strong>Stock not updated:</strong> lot{" "}
            <code style={{ background: "#FDE68A", padding: "1px 5px", borderRadius: 3 }}>
              {stockWarning.lotNumber}
            </code>{" "}
            isn't in inventory. Tell the inventory manager or fix the lot number.
            <div style={{ fontSize: 11, opacity: 0.85, marginTop: 3 }}>
              The treatment was recorded normally — only the stock count is affected.
            </div>
          </div>
          <button
            type="button"
            data-testid="dismiss-stock-warning"
            onClick={() => setStockWarning(null)}
            aria-label="Dismiss"
            style={{
              background: "transparent", border: "none", color: "#78350F",
              cursor: "pointer", padding: 2, flexShrink: 0, lineHeight: 1,
            }}
          >
            <X size={15} />
          </button>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Syringe size={15} color="#2E8B98" />
        <strong style={{ fontSize: 13 }}>Treatment administration &amp; QC</strong>
        <span style={{ fontSize: 11, color: "#9aa4b2" }}>
          documented cell count / dose + lot QC certificate
        </span>
      </div>

      {err && <div style={errBox}><AlertCircle size={13} /> {err}</div>}
      {msg && <div style={okBox}><CheckCircle2 size={13} /> {msg}</div>}

      {/* Record form */}
      {canWrite && (
        <div style={{ background: "#FAFBFC", border: "1px solid #f0f3f7", borderRadius: 6, padding: 10, marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
            Record what was administered
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <label style={lbl}>Product</label>
              <select value={form.modality} onChange={set("modality")} style={inp}>
                {MODALITIES.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Route</label>
              <select value={form.route} onChange={set("route")} style={inp}>
                {ROUTES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Amount administered</label>
              <input type="number" min="0" value={form.administeredCount} onChange={set("administeredCount")} style={inp} placeholder="e.g. 100" />
            </div>
            <div>
              <label style={lbl}>Unit</label>
              <select value={form.countUnit} onChange={set("countUnit")} style={inp}>
                {UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Viability %</label>
              <input type="number" min="0" max="100" step="0.1" value={form.viabilityPct} onChange={set("viabilityPct")} style={inp} placeholder="e.g. 97.8" />
            </div>
            <div>
              <label style={lbl}>Lot number</label>
              <input type="text" value={form.lotNumber} onChange={set("lotNumber")} style={inp} placeholder="e.g. MSC-2406-A" />
            </div>
          </div>
          <label style={lbl}>Notes (optional)</label>
          <textarea rows={2} value={form.notes} onChange={set("notes")} style={{ ...inp, resize: "vertical" }} />
          {CERT_MODALITIES.has(form.modality) && form.lotNumber.trim() && (
            <div style={{ fontSize: 11, color: "#6C757D", marginTop: 4 }}>
              <FlaskConical size={11} style={{ verticalAlign: "-2px" }} /> Lot QC certificate for {form.lotNumber.trim()} can be recorded under QC certificates{canQc ? "" : " (physician/lab)"}.
            </div>
          )}
          <button onClick={record} disabled={busy} style={{ ...btn, marginTop: 8 }}>
            {busy ? <><Loader2 size={12} className="spin" /> Saving…</> : "Record administration"}
          </button>
        </div>
      )}

      {/* History */}
      {rows.length === 0 ? (
        <div style={{ fontSize: 12, color: "#9aa4b2", fontStyle: "italic" }}>No administrations recorded yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {rows.map((r) => (
            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "1px solid #f0f3f7", borderRadius: 6 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: "#1F2937" }}>
                  {(MODALITIES.find((m) => m.v === r.modality) || {}).label || r.modality}
                  {r.administered_count != null && <span> · {Number(r.administered_count).toLocaleString()} {(UNITS.find((u) => u[0] === r.count_unit) || [])[1] || ""}</span>}
                  {r.viability_pct != null && <span> · {r.viability_pct}% viable</span>}
                </div>
                <div style={{ fontSize: 11, color: "#6C757D" }}>
                  {r.administered_at ? new Date(r.administered_at).toLocaleDateString() : ""}
                  {r.lot_number ? ` · lot ${r.lot_number}` : ""}
                  {r.administered_by_name ? ` · ${r.administered_by_name}` : ""}
                </div>
              </div>
              {r.lot_number && (
                r.qc_certificate_id ? (
                  <button onClick={() => openCertDoc(r.qc_certificate_id)} style={btnGhost} title="QC certificate on file">
                    <FileCheck2 size={12} color="#15803D" /> QC on file
                  </button>
                ) : (
                  CERT_MODALITIES.has(r.modality) && <span style={{ fontSize: 11, color: "#B45309", fontWeight: 700 }}>QC cert missing</span>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const lbl = { display: "block", fontSize: 11, fontWeight: 600, color: "#3a4a5e", margin: "6px 0 2px" };
const inp = {
  width: "100%", padding: "6px 8px", fontSize: 12, color: "#1f2a3b",
  border: "1px solid #c9d6e8", borderRadius: 4, background: "#fff", boxSizing: "border-box", fontFamily: "inherit",
};
const btn = {
  padding: "6px 12px", fontSize: 12, fontWeight: 600, background: "#2E8B98", color: "#fff",
  border: "none", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
};
const btnGhost = {
  padding: "4px 10px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#5a6478",
  border: "1px solid #DEE2E6", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0,
};
const errBox = { display: "flex", alignItems: "center", gap: 6, margin: "6px 0", padding: "6px 10px", borderRadius: 4, fontSize: 12, background: "#fde8e8", border: "1px solid #f5a5a5", color: "#8b0000" };
const okBox = { display: "flex", alignItems: "center", gap: 6, margin: "6px 0", padding: "6px 10px", borderRadius: 4, fontSize: 12, background: "#e9f5e9", border: "1px solid #a5d5a5", color: "#2d6b2d" };
